import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, Grid, ListItem } from '../../../shared'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'

interface JobChargeLaborRow {
    id: string
    code: string
    description: string
    baseRate: number
    otPercent: number
    rate50: number
    tax: number
    fringe: number
    workersComp: number
    total: number
    hbCostPerHour: number
}

interface JobChargeEquipmentRow {
    id: string
    code: string
    vistaCategory: string
    description: string
    unit: string
    operatorCode: string
    rent: number
    eoe: number
    total: number
}

@Component({
    selector: 'job-charge-rates',
    templateUrl: './job-charge-rates.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class JobChargeRatesComponent extends BaseComponent {
    // TODO: Circle back on exporting these rates to Vista (JC Fixed Rate Template and EM Rev Rates by Category).
    job = ''
    estimateCode = ''
    jobOptions: ListItem<string>[] = []
    estimateOptions: ListItem<string>[] = []
    private syncingChoices = false
    taxPercent = 8.65
    wcPercent = 5.016
    fringe = 6.6
    opFringe = 10.48
    loaded = false
    loading = false
    loadError = ''
    jobLabel = ''
    laborGrid: Grid<JobChargeLaborRow>
    equipmentGrid: Grid<JobChargeEquipmentRow>
    searchText = ''

    constructor() {
        super()
        this.laborGrid = new Grid<JobChargeLaborRow>(this.services, 'id', this)
            .filterBy(item => [item.code, item.description])
            .wrapColumnHeaders()
        this.equipmentGrid = new Grid<JobChargeEquipmentRow>(this.services, 'id', this)
            .filterBy(item => [item.code, item.vistaCategory, item.description, item.operatorCode])
            .wrapColumnHeaders()
        this.loadChoices()
        this.initialLoadingComplete()
    }

    jobChanged(job: string) {
        if (this.syncingChoices) return
        this.job = job ?? ''
        if (!this.job) {
            this.setEstimate('')
            return
        }
        const match = this.jobOptions.find(item => item.value === this.job)
        if (!match) return
        const bid = typeof match.tag === 'string' ? match.tag : ''
        this.setEstimate(this.estimateOptions.some(item => item.value === bid) ? bid : '')
    }

    estimateChanged(estimateCode: string) {
        if (this.syncingChoices) return
        this.estimateCode = estimateCode ?? ''
        if (!this.estimateCode) {
            this.setJob('')
            return
        }
        if (!this.estimateOptions.some(item => item.value === this.estimateCode)) return
        const matches = this.jobOptions
            .filter(item => item.tag === this.estimateCode)
            .sort((a, b) => a.value.length - b.value.length || a.value.localeCompare(b.value))
        if (matches.some(item => item.value === this.job)) return
        this.setJob(matches[0]?.value ?? '')
    }

    onSearchChanged(value: string) {
        this.searchText = value ?? ''
        this.laborGrid.filter = this.searchText
        this.equipmentGrid.filter = this.searchText
    }

    load() {
        const job = this.job.trim()
        if (!job) {
            this.loadError = this.estimateCode ? 'This estimate is not linked to a Vista job.' : 'Select a job.'
            return
        }
        if (this.loading) return

        this.loading = true
        this.loadError = ''
        let params = new HttpParams().set('job', job)
        const estimateCode = this.estimateCode.trim()
        if (estimateCode) params = params.set('estimateCode', estimateCode)
        params = this.setNumber(params, 'taxPct', this.taxPercent)
        params = this.setNumber(params, 'wcPct', this.wcPercent)
        params = this.setNumber(params, 'fringe', this.fringe)
        params = this.setNumber(params, 'opFringe', this.opFringe)

        this.cancelableRequest(
            'jobChargeRates',
            true,
            this.services.data.load(true, false, this.services.data.get('/api/BidRate/GetJobChargeRates', params)).subscribe({
                next: result => {
                    const payload = result[0] as {
                        job: string
                        jobDescription: string
                        estimateCode: string
                        estimateName: string
                        labor: any[]
                        equipment: any[]
                    }
                    const description = payload.jobDescription ? ` ${payload.jobDescription}` : ''
                    const estimateName = payload.estimateName ? ` ${payload.estimateName}` : ''
                    this.jobLabel = `${payload.job ?? job}${description} — HeavyBid ${payload.estimateCode ?? ''}${estimateName}`.trim()
                    this.laborGrid.items = (payload.labor ?? []).map(row => this.toLabor(row))
                    this.equipmentGrid.items = (payload.equipment ?? []).map(row => this.toEquipment(row))
                    this.laborGrid.filter = this.searchText
                    this.equipmentGrid.filter = this.searchText
                    this.loaded = true
                    this.loading = false
                },
                error: error => {
                    this.loading = false
                    this.loadError = this.errorMessage(error)
                },
            }),
        )
    }

    private toLabor(row: any): JobChargeLaborRow {
        return {
            id: row.code,
            code: row.code,
            description: row.description,
            baseRate: row.baseRate,
            otPercent: row.otPercent,
            rate50: row.rate50,
            tax: row.tax,
            fringe: row.fringe,
            workersComp: row.workersComp,
            total: row.total,
            hbCostPerHour: row.hbCostPerHour,
        }
    }

    private toEquipment(row: any): JobChargeEquipmentRow {
        return {
            id: row.code,
            code: row.code,
            vistaCategory: row.vistaCategory,
            description: row.description,
            unit: row.unit,
            operatorCode: row.operatorCode,
            rent: row.rent,
            eoe: row.eoe,
            total: row.total,
        }
    }

    private loadChoices() {
        this.cancelableRequest(
            'jobChargeChoices',
            true,
            this.services.data.load(false, false, this.services.data.get('/api/BidRate/GetJobChargeChoices')).subscribe({
                next: result => {
                    const payload = result[0] as {
                        jobs: { job: string; description: string; bidNumber: string }[]
                        estimates: { prefix: string; name: string }[]
                    }
                    this.jobOptions = (payload.jobs ?? []).map(
                        job => new ListItem(job.job, this.choiceLabel(job.job, job.description), job.bidNumber ?? ''),
                    )
                    this.estimateOptions = (payload.estimates ?? []).map(
                        estimate => new ListItem(estimate.prefix, this.choiceLabel(estimate.prefix, estimate.name)),
                    )
                },
                error: error => {
                    this.loadError = this.errorMessage(error)
                },
            }),
        )
    }

    private setJob(job: string) {
        if (job === this.job) return
        this.syncingChoices = true
        this.job = job
        this.syncingChoices = false
    }

    private setEstimate(estimateCode: string) {
        if (estimateCode === this.estimateCode) return
        this.syncingChoices = true
        this.estimateCode = estimateCode
        this.syncingChoices = false
    }

    private choiceLabel(code: string, name: string): string {
        const id = (code ?? '').trim()
        const label = (name ?? '').trim()
        if (!id) return label
        if (!label || label.toLowerCase() === id.toLowerCase()) return id
        return `${id} ${label}`
    }

    private setNumber(params: HttpParams, name: string, value: number): HttpParams {
        const parsed = Number(value)
        if (!Number.isFinite(parsed)) return params
        return params.set(name, String(parsed))
    }

    private errorMessage(error: any): string {
        const body = error?.error
        if (typeof body === 'string' && body.trim()) return body
        if (typeof body?.detail === 'string' && body.detail.trim()) return body.detail
        if (typeof body?.title === 'string' && body.title.trim()) return body.title
        return 'Job charge rates could not be loaded.'
    }
}
